import type { TableFilterState, TableSortingState } from '@ibsys/kit';
import { useCallback } from 'react';
import type {
  TableContentSearchState,
  TableHeaderOrientation,
  TableSearchState,
  TableSettings,
  TableSidePanel,
} from '../types';
import { useLocalStorageDriver } from './useLocalStorageDriver';
import type { TableStatePersistence } from './types';
import { useUrlDriver } from './useUrlDriver';

/** Начальные значения состояния таблицы, применяемые при первом посещении (до сохранения пользователем) */
type TableStateDefaults = {
  /** Сортировка по умолчанию — используется только если пользователь ещё не задавал сортировку */
  sorting?: TableSortingState;
};

/**
 * Хук управления состоянием таблицы: фильтры, сортировка, пагинация, поиск.
 *
 * Поддерживает две стратегии персистенции через внутренние драйверы:
 * - `'url'` (по умолчанию) — состояние хранится в URL search params;
 *   подходит для страниц, где нужна воспроизводимая ссылка на текущий вид
 * - `'localStorage'` — состояние хранится в пользовательских настройках через {@link useSettings};
 *   подходит для таблиц, где фильтры должны сохраняться между сессиями
 *
 * Оба драйвера всегда инициализируются (требование Rules of Hooks),
 * активный выбирается по значению `persistence`.
 *
 * @param name - Уникальное имя таблицы: префикс URL-параметров или ключ в `settings.tables`
 * @param persistence - Стратегия персистенции, по умолчанию `'url'`
 * @param defaults - Начальные значения, применяемые при первом посещении (только для `'localStorage'`)
 */
export const useTableState = (name: string, persistence: TableStatePersistence = 'url', defaults?: TableStateDefaults) => {
  const urlDriver = useUrlDriver(name);
  const localStorageDriver = useLocalStorageDriver(name, defaults?.sorting);

  const {
    filters,
    sorting,
    offset,
    search,
    contentSearch,
    columnOrder,
    columnVisibility,
    columnPinning,
    columnSizing,
    columnsPanelWidth,
    columnsPanelExpandedGroups,
    activeSidePanel,
    sidePanelPinned,
    headerOrientation,
    scrollIndex,
    scrollKey,
    dismissedSelectionKey,
    selectionDescription,
    write,
    path,
  } = persistence === 'url' ? urlDriver : localStorageDriver;

  const setFilters = useCallback(
    (value: TableFilterState | ((prev: TableFilterState) => TableFilterState)) => {
      const next = typeof value === 'function' ? value(filters) : (value ?? []);
      write({ filters: next });
    },
    [filters, write],
  );

  const setSorting = useCallback(
    (value: TableSortingState | ((prev: TableSortingState) => TableSortingState)) => {
      const next = typeof value === 'function' ? value(sorting) : value;
      write({ sorting: next });
    },
    [sorting, write],
  );

  const setOffset = useCallback(
    (data: { offset: number; page: number; pageOffset: number }) => {
      write({ offset: data.offset > 0 ? data.offset : undefined });
    },
    [write],
  );

  const setSearch = useCallback(
    (value: TableSearchState | null) => {
      write({ search: value ?? undefined });
    },
    [write],
  );

  const setContentSearch = useCallback(
    (value: TableContentSearchState | null) => {
      write({ contentSearch: value ?? null });
    },
    [write],
  );

  const setColumnState = useCallback(
    (state: Pick<TableSettings, 'columnOrder' | 'columnVisibility' | 'columnPinning' | 'columnSizing'>) => {
      write(state);
    },
    [write],
  );

  // Ширина панели управления колонками — настройка таблицы, а не её вида: в пресет не попадает.
  const setColumnsPanelWidth = useCallback(
    (width: number) => {
      write({ columnsPanelWidth: width });
    },
    [write],
  );

  // Раскрытие групп колонок — способ работы с панелью, а не вид таблицы: в пресет не попадает.
  const setColumnsPanelExpandedGroups = useCallback(
    (groups: string[]) => {
      write({ columnsPanelExpandedGroups: groups });
    },
    [write],
  );

  // Боковая панель управления — часть раскладки таблицы, а не её вида: в пресет не попадает.
  // Хранится отклонение от умолчания, поэтому закрытая панель и снятое закрепление из настроек
  // вычёркиваются, а не пишутся значением.
  const setActiveSidePanel = useCallback(
    (panel: TableSidePanel | null) => {
      write({ activeSidePanel: panel ?? undefined });
    },
    [write],
  );

  const setSidePanelPinned = useCallback(
    (pinned: boolean) => {
      write({ sidePanelPinned: pinned || undefined });
    },
    [write],
  );

  // Направление текста заголовков колонок — вид таблицы: попадает в снимок пресета.
  const setHeaderOrientation = useCallback(
    (orientation: TableHeaderOrientation) => {
      write({ headerOrientation: orientation });
    },
    [write],
  );

  // Позиция прокрутки хранится вместе с ключом состава списка, в котором она снята: в другом
  // составе строки с этим индексом может не быть, и восстанавливать позицию по нему нельзя.
  const setScrollIndex = useCallback(
    (index: number, compositionKey: string) => {
      const isRestorable = index > 0;
      write({
        scrollIndex: isRestorable ? index : undefined,
        scrollKey: isRestorable ? compositionKey : undefined,
      });
    },
    [write],
  );

  const setDismissedSelectionKey = useCallback(
    (key: string | null) => {
      write({ dismissedSelectionKey: key ?? undefined });
    },
    [write],
  );

  const setSelectionDescription = useCallback(
    (value: { key: string; text: string; source?: string } | null) => {
      write({ selectionDescription: value ?? undefined });
    },
    [write],
  );

  return {
    settings: {
      filters,
      sorting,
      search,
      contentSearch,
      offset,
      initialOffset: offset ?? 0,
      columnOrder,
      columnVisibility,
      columnPinning,
      columnSizing,
      columnsPanelWidth,
      columnsPanelExpandedGroups,
      activeSidePanel,
      sidePanelPinned,
      headerOrientation,
      scrollIndex,
      scrollKey,
      dismissedSelectionKey,
      selectionDescription,
    },
    actions: {
      onFiltersChange: setFilters,
      onSortingChange: setSorting,
      onOffsetChange: setOffset,
      setSearch,
      setContentSearch,
      setColumnState,
      setColumnsPanelWidth,
      setColumnsPanelExpandedGroups,
      setActiveSidePanel,
      setSidePanelPinned,
      setHeaderOrientation,
      setScrollIndex,
      setDismissedSelectionKey,
      setSelectionDescription,
    },
    path,
  };
};
